import React from 'react'
import { Link, Navigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight, Phone, Stethoscope, AlertTriangle, BookOpen } from 'lucide-react'
import SEOHead from '@/components/SEO/SEOHead'
import PageHeader from '@/components/Common/PageHeader'
import { findSpecies } from '@/data/species'
import { dogDiseases, catDiseases } from '@/data/diseases'
import { allBreeds } from '@/data/breeds'
import { contactInfo } from '@/data/contact'
import { toTelHref } from '@/utils/phone'
import { buildGraph, breadcrumbListData, collectionPageSchema } from '@/data/structured-data'

interface SpeciesHubProps {
  speciesKey: 'kedi' | 'kopek'
}

const SpeciesHub: React.FC<SpeciesHubProps> = ({ speciesKey }) => {
  const sp = findSpecies(speciesKey)

  if (!sp) return <Navigate to="/" replace />

  const isCat = sp.key === 'kedi'
  const diseases = isCat ? catDiseases : dogDiseases
  const breeds = allBreeds.filter(b => b.type === (isCat ? 'cat' : 'dog'))
  const diseaseCategory = isCat ? 'kedi' : 'kopek'
  const telHref = toTelHref(contactInfo.phone)

  return (
    <>
      <SEOHead
        title={sp.metaTitle}
        description={sp.metaDescription}
        keywords={sp.keywords.join(', ')}
        canonicalUrl={`/${sp.key}`}
        ogImage={sp.image}
        structuredData={buildGraph(
          collectionPageSchema({
            name: sp.heroTitle,
            description: sp.metaDescription,
            url: `/${sp.key}`,
            items: [
              ...sp.topics.map(t => ({ name: t.title, url: `/${sp.key}/${t.slug}` })),
              { name: `${sp.label} hastalıkları`, url: `/hastaliklari/${diseaseCategory}` }
            ]
          }),
          breadcrumbListData([
            { name: 'Ana Sayfa', url: '/' },
            { name: sp.heroTitle, url: `/${sp.key}` }
          ])
        )}
      />

      <PageHeader title={sp.heroTitle} subtitle={sp.heroSubtitle} />

      <section className="section-padding">
        <div className="container-custom">
          {/* Ana konular */}
          <h2 className="text-2xl font-bold mb-6">En çok sorulan konular</h2>
          <div className="grid md:grid-cols-3 gap-6 mb-14">
            {sp.topics.map((t, i) => (
              <motion.div
                key={t.slug}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.07 }}
              >
                <Link
                  to={`/${sp.key}/${t.slug}`}
                  className="group block h-full p-6 rounded-2xl bg-white border border-gray-200 hover:border-primary-300 hover:shadow-xl transition-all"
                >
                  <div className="inline-flex p-2.5 rounded-xl bg-primary-100 mb-4">
                    <Stethoscope className="w-5 h-5 text-primary-600" />
                  </div>
                  <h3 className="font-bold text-lg text-gray-900 group-hover:text-primary-700 mb-2">
                    {t.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-4">{t.description}</p>
                  <span className="text-sm font-semibold text-primary-600 inline-flex items-center gap-1">
                    Devamını oku <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Hastalıklar */}
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-2xl font-bold">{sp.label} hastalıkları</h2>
                <Link
                  to={`/hastaliklari/${diseaseCategory}`}
                  className="text-sm font-semibold text-primary-600 hover:underline whitespace-nowrap"
                >
                  {diseases.length} hastalık →
                </Link>
              </div>
              <p className="text-gray-700 leading-relaxed mb-5">
                {sp.labelPlural}de sık görülen hastalıkların belirtileri, tedavi yaklaşımları ve
                korunma yolları. Belirtileri tanımak, erken müdahale için en önemli adımdır.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {diseases.slice(0, 10).map(d => (
                  <Link
                    key={d.id}
                    to={`/hastaliklari/${diseaseCategory}/${d.slug}`}
                    className="flex items-center gap-2 p-3 rounded-lg border border-gray-200 text-sm text-gray-700 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                  >
                    <BookOpen className="w-4 h-4 text-primary-500 shrink-0" />
                    <span className="line-clamp-1">{d.title}</span>
                  </Link>
                ))}
              </div>
            </div>

            <aside className="space-y-6">
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
                <h3 className="font-bold text-gray-900 mb-2">Randevu ve bilgi</h3>
                <p className="text-sm text-gray-600 mb-4">
                  {sp.label} sağlığıyla ilgili her konuda arayabilirsiniz. Şanlıurfa Karaköprü’deki
                  kliniğimizde muayene, aşı ve operasyon hizmeti veriyoruz.
                </p>
                <a href={telHref} className="btn-primary w-full inline-flex items-center justify-center gap-2">
                  <Phone className="w-4 h-4" /> {contactInfo.phone}
                </a>
                <Link
                  to="/karakopru-veteriner"
                  className="text-sm font-semibold text-primary-700 hover:underline mt-4 inline-block"
                >
                  Hizmet verdiğimiz mahalleler →
                </Link>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <h3 className="font-bold text-red-900">Acil belirtiler</h3>
                </div>
                <ul className="text-sm text-red-800 space-y-1.5 list-disc list-inside">
                  <li>Nefes almakta zorlanma</li>
                  <li>Tekrarlayan kusma veya kanlı ishal</li>
                  <li>İdrar yapamama</li>
                  <li>Havale, bilinç kaybı</li>
                  <li>Zehirlenme şüphesi, travma</li>
                </ul>
                <Link to="/acil-veteriner" className="text-sm font-semibold text-red-700 hover:underline mt-3 inline-block">
                  7/24 acil veteriner →
                </Link>
              </div>
            </aside>
          </div>

          {/* Irklar */}
          {breeds.length > 0 && (
            <div className="mt-14">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-2xl font-bold">{sp.label} ırkları</h2>
                <Link to="/pets" className="text-sm font-semibold text-primary-600 hover:underline">
                  Tümü →
                </Link>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {breeds.slice(0, 8).map(b => (
                  <Link
                    key={b.id}
                    to="/pets"
                    className="group rounded-xl overflow-hidden border border-gray-200 hover:shadow-lg transition-all"
                  >
                    <img
                      src={b.image}
                      alt={b.name}
                      loading="lazy"
                      className="w-full h-28 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="p-3 text-sm font-medium text-gray-800 group-hover:text-primary-700">
                      {b.name}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  )
}

export default SpeciesHub
